import { Badge, Button } from '@mantine/core';
import classnames from 'classnames';
import { features } from '../../Pricing.data';
import {
  ActionGroup,
  BadgeContainer,
  Category,
  CategoryFeatures,
  CategoryHeader,
  ContactUs,
  FeatureList,
  FeaturesPrefix,
  FeatureText,
  PlanDescription,
  PlanTitle,
  PreviousPriceText,
  PriceText,
  StyledCard,
  Wrapper,
} from './PricingColumn.style';

type Plan = {
  id: string;
  title: string;
  previousPrice: string;
  prefix: string;
  priceColor: string;
  altPrice: string;
  description: string;
  disabled: boolean;
  price: string;
  categories: string[];
  highlight?: boolean;
  CTA: string;
};

export type PricingColumnProps = {
  plan: Plan;
  isCurrent: boolean;
  onPlanSelect: (planId: string) => void;
};

export function PricingColumn(props: PricingColumnProps) {
  const { plan, isCurrent } = props;
  const {
    title,
    price,
    previousPrice,
    priceColor,
    disabled,
    altPrice,
    prefix,
    description,
    categories,
    highlight,
    CTA,
  } = plan;

  function onPlanSelect() {
    props.onPlanSelect(plan.id);
  }

  const checkmarkEmoji = '✅';
  const xEmoji = '✖';

  function renderFeature(feature: Json) {
    const { isOn } = feature;

    let emoji = checkmarkEmoji;

    if (typeof isOn === 'boolean') {
      emoji = isOn ? checkmarkEmoji : xEmoji;
    } else {
      emoji = isOn[plan.id] ? checkmarkEmoji : xEmoji;
    }

    const className = classnames('feature', {
      faded: emoji === xEmoji,
    });

    return (
      <FeatureText key={feature.id} className={className}>
        <span>{emoji}</span>
        {feature.title}
      </FeatureText>
    );
  }

  function renderFeatures(category: string) {
    return features
      .filter((f) => f.category === category && f.planIds.includes(plan.id))
      .map((feature: Json) => renderFeature(feature));
  }

  function renderCategory(category: string) {
    return (
      <Category key={category} className='category'>
        <CategoryHeader>{category}</CategoryHeader>
        <CategoryFeatures>{renderFeatures(category)}</CategoryFeatures>
      </Category>
    );
  }

  function renderCategories() {
    return categories.map((category: string) => renderCategory(category));
  }

  const className = classnames({
    disabled,
  });

  return (
    <Wrapper className={className}>
      <StyledCard highlight={highlight} shadow='md' radius='md'>
        {highlight && (
          <BadgeContainer>
            <Badge color='blue' variant='filled' size='lg' mb='sm'>
              Popular
            </Badge>
          </BadgeContainer>
        )}

        {isCurrent && (
          <BadgeContainer>
            <Badge color='pink' variant='filled' size='lg' mb='sm'>
              Current
            </Badge>
          </BadgeContainer>
        )}
        <PlanTitle
          variant='gradient'
          order={3}
          gradient={{ from: '#ff6a00', to: 'gold' }}
          title='Plans'
          size='25px'
        >
          {title}
        </PlanTitle>
        {previousPrice && <PreviousPriceText>${previousPrice}/mo</PreviousPriceText>}
        {price && (
          <PriceText color={priceColor}>
            ${price}
            <span>/mo</span>
          </PriceText>
        )}

        {altPrice && <ContactUs>{altPrice}</ContactUs>}
        <PlanDescription>{description}</PlanDescription>
        {prefix && <FeaturesPrefix>{prefix}</FeaturesPrefix>}
        <FeatureList spacing='xs'>{renderCategories()}</FeatureList>
        <ActionGroup>
          {CTA && (
            <Button
              onClick={onPlanSelect}
              variant={highlight ? 'filled' : 'outline'}
              color='pink'
              size='md'
              disabled={isCurrent}
            >
              {CTA}
            </Button>
          )}
        </ActionGroup>
      </StyledCard>
    </Wrapper>
  );
}
